import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Shield, FileText, Clock, CheckCircle, ArrowRight, LogOut, Building2 } from 'lucide-react';
import { getSubdomainInfo, getDepartmentFromSubdomain, loadDepartmentTheme } from '../utils/subdomainUtils';
import axios from 'axios';

const DepartmentPortal = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [department, setDepartment] = useState(null);
  const [loading, setLoading] = useState(true);

  const { subdomain } = getSubdomainInfo();

  useEffect(() => {
    fetchDepartmentData();
  }, [subdomain]);

  const fetchDepartmentData = async () => {
    if (!subdomain) {
      setLoading(false);
      return;
    }

    try {
      // First try to get department from backend
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/departments/by-subdomain/${subdomain}`
      );
      
      const deptData = response.data;
      setDepartment(deptData);
      loadDepartmentTheme(deptData);
      
    } catch (error) {
      console.error('Error fetching department data:', error);
      
      // Fallback to local department data
      const localDept = getDepartmentFromSubdomain(subdomain);
      if (localDept) {
        setDepartment(localDept);
        loadDepartmentTheme(localDept);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading department portal...</p>
        </div>
      </div>
    );
  }

  if (!department) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-red-800">Department Not Found</CardTitle>
            <CardDescription>
              The subdomain "{subdomain}" is not associated with any registered department.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/">
              <Button className="w-full">
                Go to ReqTrak Main Site
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              {department.logo_url ? (
                <img 
                  src={department.logo_url.startsWith('http') ? department.logo_url : `${process.env.REACT_APP_BACKEND_URL}${department.logo_url}`}
                  alt={`${department.name} Logo`}
                  className="h-10 w-auto object-contain"
                />
              ) : (
                <img 
                  src="https://customer-assets.emergentagent.com/job_policerecords-2/artifacts/crx2tbck_RequestTrack%20logo.png" 
                  alt="ReqTrak Logo" 
                  className="h-20 w-20 object-contain"
                />
              )}
              <div>
                <h1 className="text-lg font-bold text-slate-800">{department.name}</h1>
                <p className="text-xs text-slate-600">Public Records Portal</p>
              </div>
            </div>
            
            {user && (
              <div className="flex items-center gap-4">
                <span className="text-sm text-slate-600">Welcome, {user.full_name}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={logout}
                  className="flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {user ? (
          // Logged-in user dashboard
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-slate-800 mb-4">
                Welcome to Your Records Portal
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Access and manage your public records requests with {department.name}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => navigate('/request/new')}
                  className="px-8 py-3 text-lg"
                  style={{ 
                    backgroundColor: department.primary_color,
                    borderColor: department.primary_color
                  }}
                >
                  Submit New Request
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="px-8 py-3 text-lg"
                >
                  View My Requests
                </Button>
              </div>
            </div>
          </div>
        ) : (
          // Public portal for non-logged-in users
          <div className="space-y-12">
            {/* Hero Section */}
            <div className="text-center">
              <Badge className="mb-4" style={{ backgroundColor: `${department.primary_color}20`, color: department.primary_color }}>
                {department.name}
              </Badge>
              <h2 className="text-4xl font-bold text-slate-800 mb-4">
                Public Records Request Portal
              </h2>
              <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                Submit and track public records requests with {department.name}. 
                Our secure portal makes it easy to access the information you need.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/login">
                  <Button 
                    className="px-8 py-3 text-lg text-white"
                    style={{ 
                      backgroundColor: department.primary_color,
                      borderColor: department.primary_color
                    }}
                  >
                    Sign In
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    variant="outline"
                    className="px-8 py-3 text-lg"
                    style={{ 
                      borderColor: department.primary_color,
                      color: department.primary_color
                    }}
                  >
                    Create Account
                  </Button>
                </Link>
              </div>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-blue-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <FileText className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Submit Requests</h3>
                <p className="text-slate-600">
                  Easily submit public records requests online with our simple form process.
                </p>
              </div>
              
              <div className="text-center">
                <div className="bg-green-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Clock className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Track Progress</h3>
                <p className="text-slate-600">
                  Monitor the status of your requests in real-time through your personal dashboard.
                </p>
              </div>
              
              <div className="text-center">
                <div className="bg-purple-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Secure Access</h3>
                <p className="text-slate-600">
                  Your information is protected with enterprise-grade security and encryption.
                </p>
              </div>
            </div>

            {/* Contact Information */}
            <Card className="max-w-2xl mx-auto">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Department Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p><strong>Department:</strong> {department.name}</p>
                  {department.department_email && (
                    <p><strong>Email:</strong> {department.department_email}</p>
                  )}
                  {department.department_phone && (
                    <p><strong>Phone:</strong> {department.department_phone}</p>
                  )}
                  {department.department_address && (
                    <p><strong>Address:</strong> {department.department_address}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div>
              <p className="text-sm">
                © 2025 {department.name}. All rights reserved.
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Powered by ReqTrak Platform
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <Link 
                to="/" 
                className="text-sm text-slate-400 hover:text-white transition-colors"
              >
                ReqTrak Platform
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DepartmentPortal;